import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Stack, Button, Alert, LinearProgress, CircularProgress } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PsychologistApi from "../../api/PsychologistApi";
import SchedulingApi from "../../api/SchedulingApi";
import AvailabilityEditor, { Block } from "../AvailabilityEditor";
import CalendarSync from "../CalendarSync";
import BasicsSection from "./BasicsSection";
import OfferSection from "./OfferSection";
import CredentialsSection from "./CredentialsSection";
import BillingSection from "./BillingSection";
import DocumentsSection from "./DocumentsSection";
import { emptyProfile, profileFromApi, credentialsComplete, billingComplete } from "./profileShared";
import { FormControlLabel, Checkbox, Link } from "@mui/material";

const STEPS = ["basics", "offer", "credentials", "documents", "billing", "availability", "calendar", "done"] as const;
type StepKey = (typeof STEPS)[number];

/** Guided setup na registratie: het profiel in behapbare stappen (basis →
 *  aanbod → erkenning → documenten → facturatie), dan beschikbaarheid en de
 *  optionele agenda-koppeling. Elke stap bewaart meteen, dus halverwege
 *  stoppen verliest niets. Afsluiten zet onboardingCompletedAt — tot dan
 *  toont het dashboard deze wizard. */
export default function ProviderOnboarding({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(emptyProfile());
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [docCount, setDocCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  // Terugkeer uit de agenda-OAuth-redirect (…/dashboard?calendar=…) landt
  // opnieuw in de wizard — spring dan direct terug naar de agenda-stap.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [step, setStep] = useState(() =>
    new URLSearchParams(window.location.search).get("calendar") ? STEPS.indexOf("calendar") : 0
  );
  const key: StepKey = STEPS[step];

  useEffect(() => {
    Promise.all([
      PsychologistApi.getMine().then((p) => { if (p) setProfile(profileFromApi(p)); }).catch(() => {}),
      SchedulingApi.getMyAvailability().then((a) => setBlocks(a.weeklyBlocks || [])).catch(() => {}),
    ]).finally(() => setLoaded(true));
  }, []);

  const saveProfile = async () => {
    await PsychologistApi.updateMine({ ...profile, rateCents: profile.coachingRateCents ?? profile.rateCents });
  };

  const next = async () => {
    setErr("");
    setSaving(true);
    try {
      if (key === "basics" || key === "offer" || key === "credentials" || key === "billing") await saveProfile();
      if (key === "availability") await SchedulingApi.setMyAvailability({ weeklyBlocks: blocks, exceptions: [] });
      setStep(step + 1);
    } catch (e: any) {
      setErr(e?.response?.data?.error || t("vetting.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const finish = async () => {
    setErr("");
    setSaving(true);
    try {
      await PsychologistApi.completeOnboarding();
      onDone();
    } catch (e: any) {
      setErr(e?.response?.data?.error || t("vetting.saveError"));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <Box sx={{ minHeight: "40dvh", display: "grid", placeItems: "center" }}><CircularProgress sx={{ color: "primary.main" }} /></Box>;

  const nextDisabled =
    saving ||
    (key === "basics" && !profile.displayName.trim()) ||
    (key === "offer" && (profile.specializations.length === 0 || profile.regionsServed.length === 0 || profile.languages.length === 0 || (profile.offersCoaching !== false && profile.coachingTherapeuticPurpose === undefined))) ||
    (key === "credentials" && !credentialsComplete(profile)) ||
    (key === "documents" && docCount === 0) ||
    (key === "billing" && !billingComplete(profile)) ||
    (key === "availability" && blocks.length === 0);
  // Uitstelbaar: documenten en beschikbaarheid kunnen ook later in het
  // dashboard (de agenda-stap blokkeert nooit, dus die heeft geen skip nodig).
  const canSkip = key === "documents" || key === "availability";

  const content: Record<StepKey, React.ReactNode> = {
    basics: <BasicsSection profile={profile} onChange={setProfile} />,
    offer: <OfferSection profile={profile} onChange={setProfile} />,
    credentials: <CredentialsSection profile={profile} onChange={setProfile} />,
    documents: <DocumentsSection onCountChange={setDocCount} />,
    billing: <BillingSection profile={profile} onChange={setProfile} />,
    availability: <AvailabilityEditor blocks={blocks} onChange={setBlocks} />,
    calendar: <CalendarSync />,
    done: (
      <Stack spacing={2} sx={{ alignItems: "flex-start" }}>
        <CheckCircleRoundedIcon sx={{ fontSize: 48, color: "success.main" }} />
        <Typography sx={{ m: 0 }}>{t("onboarding.done.review")}</Typography>
        {/* Klik-akkoord = ondertekening van de samenwerkingsovereenkomst
            (§11; versioned gelogd in de consent-ledger, server-side vereist). */}
        <FormControlLabel
          control={<Checkbox checked={termsAccepted} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTermsAccepted(e.target.checked)} />}
          label={
            <Typography sx={{ fontSize: ".95rem" }}>
              {t("onboarding.done.acceptTerms")}{" "}
              <Link href="/psycholoog-voorwaarden" target="_blank" rel="noreferrer">{t("onboarding.done.termsLink")}</Link>
            </Typography>
          }
        />
      </Stack>
    ),
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <LinearProgress variant="determinate" value={((step + 1) / STEPS.length) * 100}
          sx={{ flex: 1, height: 6, borderRadius: 999, bgcolor: "var(--primary-100)", "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: "primary.main" } }} />
        <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 34, textAlign: "right" }}>{step + 1}/{STEPS.length}</Typography>
      </Box>

      <Typography variant="h1" sx={{ fontSize: { xs: "1.6rem", sm: "2rem" }, mb: 1 }}>{t(`onboarding.${key}.title`)}</Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }}>{t(`onboarding.${key}.subtitle`)}</Typography>

      <Box sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)", mb: 3 }}>
        {content[key]}
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr("")}>{err}</Alert>}

      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        {step > 0 && key !== "done" && (
          <Button onClick={() => { setErr(""); setStep(step - 1); }} sx={{ color: "text.secondary" }}>{t("common.back")}</Button>
        )}
        <Box sx={{ flex: 1 }} />
        {canSkip && nextDisabled && !saving && (
          <Button onClick={() => { setErr(""); setStep(step + 1); }} sx={{ color: "text.secondary" }}>{t("common.skip")}</Button>
        )}
        {key === "done" ? (
          <Button variant="contained" size="large" onClick={finish} disabled={saving || !termsAccepted}>{t("onboarding.done.cta")}</Button>
        ) : (
          <Button variant="contained" size="large" onClick={next} disabled={nextDisabled}>{t("common.next")}</Button>
        )}
      </Stack>
    </Container>
  );
}
